import Add from '@mui/icons-material/Add';
import CancelRounded from '@mui/icons-material/CancelRounded';
import ErrorOutlined from '@mui/icons-material/ErrorOutlined';
import { Backdrop, Box, createTheme as createThemeMui, ThemeProvider, useMediaQuery, useTheme } from '@mui/material';
import Avatar from '@mui/material/Avatar';
import Fab from '@mui/material/Fab';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Button from '/@/components/button';
import { getLabelStep } from '/@/components/crop-monitoring/progress-bar/assets/BASE_STEPS';
import settings from '/@/core/settings';
import { useAnchorEl } from '/@/hooks/anchor-el/useAnchorEl';
import { _ } from '/@/locale';
import { getAppInstance } from '/@/store/session/selectors';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import { reverse } from '/@/utils/router';
import Alert from '../../bayer/alert';
import ExpertoBayerCatalogAction from '../../experto-bayer-action';
import colors from '../../theme-provider/colors';
import NitrogenBot from './assets/avatar.svg';

/**
 * @param {{
 * vegetativeStage?: number | null
 * phenology?: 'emergence' | 'flowering' | 'maturity' | null
 * showNew?: boolean
 * cropMonitoringId?: number | false
 * }} props
 * */
export default function NitrogenSmartFloatingButton({
    vegetativeStage,
    phenology,
    showNew,
    cropMonitoringId,
    nitrogenRecommendations = [],
}) {
    const enabled = useSelector((state) => getAppInstance(state).enableNitogrenRecommendation);
    const showWarning = vegetativeStage < 6;
    const history = useNavigate();
    const baseTheme = useTheme();
    const theme = createThemeMui({
        typography: {
            ...baseTheme.typography,
            button: {
                textTransform: 'none',
                fontSize: 14,
            },
        },
        palette: {
            primary: {
                main:
                    settings.app.theme === 'expertobayer'
                        ? colors.themes[settings.app.theme].primary.default
                        : '#FF7A00',
                light: settings.app.theme === 'expertobayer' ? '#ffffff' : '#FFA800',
                contrastText: 'white',
            },
        },
    });
    const [hover, setHover] = React.useState(false);

    const downLg = useMediaQuery((theme) => theme.breakpoints.down('lg'));
    const [{ anchorEl, open }, { handleClick, handleClose }] = useAnchorEl();

    const id = open ? 'simple-popover' : undefined;

    if (!enabled) return null;

    const stepLabel = getLabelStep(vegetativeStage, phenology);

    const handleViewRecomendation =
        nitrogenRecommendations.length === 0
            ? undefined
            : () => {
                  history(
                      reverse('nitrogenRecommendation.NitrogenRecommendationList', {
                          qs: { cropMonitoringId },
                      })
                  );
              };

    return (
        <React.Fragment>
            <ThemeProvider theme={theme}>
                <Box
                    sx={{
                        position: 'fixed',
                        zIndex: 100,
                        bottom: (t) => t.spacing(4),
                        right: (t) => t.spacing(4),
                    }}
                >
                    {settings.app.theme === 'expertobayer' ? (
                        <Box onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
                            <ExpertoBayerCatalogAction
                                onClick={(e) => handleClick(e)}
                                src={NitrogenBot}
                                title={_('Crear recomendación\n N-Smart')}
                            />
                        </Box>
                    ) : (
                        <Fab
                            onClick={handleClick}
                            variant={(downLg && !hover && !open && 'circular') || 'extended'}
                            onMouseEnter={() => setHover(true)}
                            onMouseLeave={() => setHover(false)}
                            sx={{
                                position: 'relative',
                                boxShadow: 4,
                                borderRadius: '26.5px',
                                bgcolor: (t) => (open || !downLg ? t.palette.primary.light : 'white'),
                                border: (t) => `3px solid ${t.palette.primary.light}`,
                                display: 'flex',
                                justifyContent: 'center',
                                gap: 1,
                                width: 'auto',
                                height: 'auto',
                                p: 0,
                                color: 'white',
                                '&:hover': {
                                    bgcolor: (t) => t.palette.primary.light,
                                },
                                transition: (t) =>
                                    t.transitions.create(['width', 'padding', 'display'], { duration: 100 }),
                            }}
                        >
                            <ErrorOutlined
                                color='error'
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    zIndex: 10,
                                    transform: 'translateY(-50%)',
                                    right: '0rem',
                                    bgcolor: 'white',
                                    borderRadius: '100%',
                                    display: (!showWarning && 'none') || undefined,
                                }}
                            />

                            <Avatar
                                sx={{
                                    width: 40,
                                    height: 40,
                                    mr: { lg: 1 },
                                    bgcolor: (t) => (!hover || open ? 'white' : t.palette.primary.main),
                                    color: (t) => (!hover || open ? t.palette.primary.main : 'white'),
                                }}
                            >
                                N
                            </Avatar>
                            <Typography
                                sx={{
                                    display: !downLg || hover || open ? 'block' : 'none',
                                    pr: 2,
                                    fontWeight: 600,
                                    fontSize: 14,
                                    color: 'white',
                                }}
                            >
                                {_('Crear recomendación\n N-Smart')}
                            </Typography>
                        </Fab>
                    )}
                    <Backdrop>
                        <Popover
                            id={id}
                            open={open}
                            anchorEl={anchorEl}
                            onClose={handleClose}
                            anchorPosition={{ top: 100, left: 100 }}
                            anchorOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            transformOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right',
                            }}
                            sx={{
                                bgcolor: 'transparent',
                                borderRadius: 0,
                                boxShadow: (t) => `0px 0px 250px ${t.palette.primary.light}`,
                            }}
                        >
                            <Box
                                sx={{
                                    p: 2,
                                    maxWidth: '450px',
                                    bgcolor: 'white',
                                    borderRadius: 2,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1,
                                }}
                            >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant='h4' sx={{ fontWeight: 600 }}>
                                        {_('Recomendación N-Smart')}
                                    </Typography>
                                    <IconButton size='large' onClick={handleClose}>
                                        <CancelRounded fontSize='large' />
                                    </IconButton>
                                </Box>
                                <Alert
                                    severity='danger'
                                    sx={{
                                        display: (!showWarning && 'none') || undefined,
                                    }}
                                >
                                    <span
                                        dangerouslySetInnerHTML={{
                                            __html: _(
                                                'Tu cultivo se encuentra en el <strong>estadio {{stepLabel}}</strong>, recordá que una vez que lleguen a <strong>V6</strong> ya no podrás crear una <strong>recomendación N-Smart.</strong>',
                                                { stepLabel }
                                            ),
                                        }}
                                    />
                                </Alert>
                                <Typography>
                                    {_(
                                        'A través de N-Smart podrás mapear el estatus nutricional de Nitrógeno con imágenes satelitales en estadios vegetativos del cultivo. La posibilidad de crear una recomendación para este seguimiento estará disponible hasta el estadio V6 del cultivo.'
                                    )}
                                </Typography>
                                <Button
                                    variant='contained'
                                    fullWidth
                                    startIcon={<Add />}
                                    disableElevation
                                    sx={{ display: showNew ? undefined : 'none' }}
                                    onClick={() => {
                                        mpTrackEvent('N-Smart Recommendation began', {
                                            entry_source: 'crop-monitoring-floating-button',
                                        });
                                        history(
                                            reverse('nitrogenRecommendation.NitrogenRecommendationCreate', {
                                                qs: { cropMonitoringId: cropMonitoringId || undefined },
                                            })
                                        );
                                    }}
                                >
                                    {_('Nueva recomendación N-Smart')}
                                </Button>
                                {nitrogenRecommendations.length > 0 && (
                                    <Button
                                        variant='outlined'
                                        fullWidth
                                        disableElevation
                                        sx={{ display: handleViewRecomendation ? undefined : 'none' }}
                                        onClick={() => {
                                            if (typeof handleViewRecomendation === 'undefined') return;
                                            handleClose();
                                            handleViewRecomendation();
                                        }}
                                    >
                                        {_('Ver recomendaciónes generadas')}
                                    </Button>
                                )}
                                <Button
                                    variant='outlined'
                                    fullWidth
                                    disableElevation
                                    onClick={() =>
                                        window.open(
                                            reverse('library.LibraryDetail', {
                                                params: { slug: 'nitrogeno-smart-programa' },
                                            })
                                        )
                                    }
                                >
                                    {_('Quiero saber más sobre el programa')}
                                </Button>
                            </Box>
                        </Popover>
                    </Backdrop>
                </Box>
            </ThemeProvider>
        </React.Fragment>
    );
}
